/**
 * Clause-level fuzzy pass (plan §3): find every word the parser does not
 * recognize, propose corrections, and re-canonicalize the corrected stream.
 *
 * @remarks
 * Runs between canonicalization and structure matching. Only `word`-kind
 * canonical tokens whose text is outside the legal-word set (compiled
 * vocabulary + catalog words) are correction targets — everything that
 * matched exactly or via alias is untouchable (exact > alias > fuzzy,
 * absolutely). Uncorrectable words stay as typed and fail honestly
 * downstream.
 *
 * When any correction lands, the ORIGINAL raw token stream is patched by
 * span and re-canonicalized whole. That re-run is what lets a corrected word
 * rejoin larger constructs — `pikc up` corrects to `pick`, and the second
 * canonicalization re-merges the multiword alias `pick up` → `take`. Patched
 * tokens carry the corrected text as their surface (the grammar's positional
 * preposition checks read surfaces); the returned {@link TokenCorrection}s
 * keep the original text + span for UI echoes.
 *
 * The first (leftmost) near-tie aborts the clause — the orchestrator decides
 * whether it becomes a clarification or an `unrecognized` with suggestions.
 *
 * @packageDocumentation
 */

// cspell:ignore pikc msak -- deliberate player typos: the re-merge and the head-position examples need them

import { type CanonicalizationVocabulary, canonicalizeTokens, type CanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type CompiledLexicon } from '../lexicon/compile-lexicon.js';
import { foldDiacritics } from '../normalize/fold-characters.js';
import { singularFormsOf } from '../normalize/plurals.js';
import {
	type FuzzyLengthThreshold,
	type ParseCandidateEntity,
	type SourceSpan,
	type Token,
	type TokenCorrection,
} from '../types.js';
import { correctToken } from './correct-token.js';

/** The outcome of one clause's fuzzy pass. */
export type ClauseCorrectionOutcome =
	| { readonly corrections: readonly TokenCorrection[]; readonly tokens: readonly CanonicalToken[]; readonly type: 'corrected' }
	| { readonly tie: NearTie; readonly type: 'near_tie' };

/** The correction pool and word sets one parse call shares across its clauses. */
export interface FuzzyCandidatePool {
	/** Sorted, deduplicated correction targets: compiled vocabulary + catalog names (never page words — D208). */
	readonly candidateWords: readonly string[];
	/** Catalog word → the entities it belongs to — near-tie classification and clarification building. */
	readonly entitiesByWord: ReadonlyMap<string, readonly ParseCandidateEntity[]>;
	/** Every word the parser recognizes — the targets, the pages' words, the consumer's known words (D211) and the vocabulary's recognition words (D229); words in this set are never corrected. */
	readonly legalWordSet: ReadonlySet<string>;
	/**
	 * D213: the targets a word AFTER the clause's head may become — every
	 * candidate but a bare verb. A verb is head material; a word in the
	 * object slot is never one, so `take mask` in a pack that declares no
	 * mask is left as typed rather than read as `take ask`.
	 */
	readonly objectCandidateWords: readonly string[];
}

/** One near-tie: the typed word, where it sat, and the equally-close targets (sorted). */
export interface NearTie {
	readonly editDistance: number;
	readonly originalText: string;
	readonly sourceSpan: SourceSpan;
	readonly targetWords: readonly string[];
}

/**
 * Build the correction pool one parse call shares across its clauses.
 *
 * @remarks
 * D208: an entity's page words (D205's `descriptionWords`) are LEGAL words
 * here — never correction TARGETS. Before that, a word the page used and the
 * vocabulary did not know — `torn`, `wheel`, `schooner's` — was rewritten
 * into the nearest thing the pool knew before the resolver ever saw it:
 * `torn sail` became `turn sail` (a verb mid-phrase, "INSPECT could not
 * carry `turn` anywhere useful"), `wheel ruts` became `well ruts`,
 * `schooner's bow` became `schooner bow`. Calder's corpus (D206) found all
 * three shapes; they were one omission. They are not targets because a
 * correction INTO a page word would make the resolver accept a modifier the
 * player never typed — `wide` corrected to `side` and the glass examined as
 * if the player had said so — which is a guess, and the pillar forbids it.
 *
 * D211: the consumer's KNOWN words are legal too, and only legal. The
 * catalog is what is referable this turn; a word the world declares
 * somewhere else — a thing in another room — is still a word, and reading
 * it as a typo of the nearest verb (`mask` → `ask`) taught the player about
 * a second verb they never typed. Not targets, for D208's reason: a
 * correction into a word for a thing that is not here can resolve nothing
 * and would only move the failure somewhere less honest.
 *
 * D213 (Calder, playing D212): `take mask` in a pack whose prose never says
 * "mask" still read the noun as the verb `ask` — D211 closed the case only
 * where the word was known. A correction TARGET depends on where the word
 * sits: a clause's head may become any verb, and a word after the head may
 * become anything BUT one, because the grammar has one head and the player
 * has already typed it. The pool carries both lists.
 *
 * D229 (E17, the play-test): the vocabulary's RECOGNITION words are legal
 * too. `lower lead` asked "Did you mean POWER LEAD?", `feed dogs` read
 * `feel`, `score` became the shore: every one a real word, one letter from a
 * word the game knew, rewritten into a different meaning. A typo is a
 * non-word; a word the language has is left as typed and fails honestly as
 * itself ("Nothing about the lead answers to LOWER"). The vocabulary already
 * carries the list that tells English from mash (D28, `recognitionWords`),
 * and the shape ladder reads it for exactly that distinction — so the fuzzy
 * rung now agrees with the ladder about what a word is, and the base
 * vocabulary's list is where a missing word goes.
 *
 * @param lexicon - The compiled lexicon's sorted fuzzy vocabulary, its single-token intents and its recognition words
 * @param entities - The per-parse catalog (nouns, aliases, adjectives contribute correction targets; page words are legal only)
 * @param knownWords - Words the consumer knows beyond this catalog (D211) — legal only
 * @returns The pool (see {@link FuzzyCandidatePool})
 */
export const buildFuzzyCandidatePool = (
	lexicon: Pick<CompiledLexicon, 'intentsByToken' | 'recognitionWordSet' | 'vocabularyTokens'>,
	entities: readonly ParseCandidateEntity[],
	knownWords: ReadonlySet<string> = new Set(),
): FuzzyCandidatePool => {
	const entitiesByWord = new Map<string, ParseCandidateEntity[]>();

	for (const entity of entities) {
		for (const catalogName of [...entity.nouns, ...entity.aliases, ...entity.adjectives]) {
			for (const catalogWord of foldDiacritics(catalogName.trim().toLowerCase()).split(' ')) {
				const owningEntities = entitiesByWord.get(catalogWord) ?? [];

				if (!owningEntities.includes(entity)) {
					owningEntities.push(entity);
				}
				entitiesByWord.set(catalogWord, owningEntities);
			}
		}
	}

	const candidateWords = [...new Set([...lexicon.vocabularyTokens, ...entitiesByWord.keys()])].sort((leftWord, rightWord) =>
		leftWord.localeCompare(rightWord, 'en'),
	);
	const legalWordSet = new Set([
		...candidateWords,
		...entities.flatMap((entity) => entity.descriptionWords ?? []),
		...knownWords,
		...lexicon.recognitionWordSet,
	]);

	return {
		candidateWords,
		entitiesByWord,
		legalWordSet,
		// A verb that is ALSO a catalog word (a thing called "lock" beside the verb) stays: the object slot can hold it.
		objectCandidateWords: candidateWords.filter((word) => !lexicon.intentsByToken.has(word) || entitiesByWord.has(word)),
	};
};

/**
 * Whether a clause token sits where a head verb could: first, and not
 * opened by an article. `the msak` is a noun phrase however it is spelled.
 *
 * @param canonicalToken - The token, canonicalized once
 * @param index - Its position among the clause's canonical tokens
 * @returns True for the one position the grammar reads a verb from
 */
const isHeadPosition = (canonicalToken: CanonicalToken, index: number): boolean =>
	index === 0 && !(canonicalToken.kind === 'word' && canonicalToken.isNounPhraseStart === true);

/**
 * Is this word already a word — legal as typed, never a typo to correct?
 *
 * @remarks
 * D217: the regular plural of a word the catalog names a thing by is a word
 * too. `examine crates` beside two crates was one edit from `crate`, so the
 * rung "corrected" the plural into the singular and the player was asked
 * which crate — the very question a plural exists to avoid.
 *
 * @param word - The typed word, folded
 * @param pool - The parse call's correction pool
 * @returns Whether the rung must leave it as typed
 */
const isLegalWord = (word: string, pool: FuzzyCandidatePool): boolean =>
	pool.legalWordSet.has(word) || singularFormsOf(word).some((singularWord) => pool.entitiesByWord.has(singularWord));

/**
 * Run one clause's fuzzy pass.
 *
 * @param rawTokens - The clause's raw tokens (stopwords included), as tokenized
 * @param canonicalTokens - The same clause, already canonicalized once
 * @param vocabulary - The compiled vocabulary view (for re-canonicalization)
 * @param pool - The parse call's correction pool
 * @param thresholds - The active fuzzy length tiers
 * @returns The (possibly re-canonicalized) tokens plus corrections, or the leftmost near-tie
 */
export const correctClauseTokens = (
	rawTokens: readonly Token[],
	canonicalTokens: readonly CanonicalToken[],
	vocabulary: CanonicalizationVocabulary,
	pool: FuzzyCandidatePool,
	thresholds: readonly FuzzyLengthThreshold[],
): ClauseCorrectionOutcome => {
	const correctionsBySpanStart = new Map<number, TokenCorrection>();

	for (const [index, canonicalToken] of canonicalTokens.entries()) {
		// D242: a word with a space in it is a two-word binding the verb declared, read as one — never typed as one, never corrected.
		if (canonicalToken.kind !== 'word' || canonicalToken.sourceText.includes(' ') || isLegalWord(canonicalToken.sourceText, pool)) {
			continue;
		}

		const targets = isHeadPosition(canonicalToken, index) ? pool.candidateWords : pool.objectCandidateWords;
		const proposal = correctToken(canonicalToken.sourceText, targets, thresholds);

		if (proposal.type === 'near_tie') {
			return {
				tie: {
					editDistance: proposal.editDistance,
					originalText: canonicalToken.sourceText,
					sourceSpan: canonicalToken.sourceSpan,
					targetWords: proposal.targetWords,
				},
				type: 'near_tie',
			};
		}

		if (proposal.type === 'corrected') {
			correctionsBySpanStart.set(canonicalToken.sourceSpan.start, {
				correctedText: proposal.correctedText,
				editDistance: proposal.editDistance,
				originalText: canonicalToken.sourceText,
				sourceSpan: canonicalToken.sourceSpan,
			});
		}
	}

	if (correctionsBySpanStart.size === 0) {
		return { corrections: [], tokens: canonicalTokens, type: 'corrected' };
	}

	// Patch the RAW stream by span and re-canonicalize whole, so corrected
	// words can re-merge into multiword aliases and reclassify as intents or
	// directions. Spans are preserved: echoes still point at what was typed.
	const patchedRawTokens = rawTokens.map((rawToken) => {
		const correction = correctionsBySpanStart.get(rawToken.sourceSpan.start);

		return correction === undefined ? rawToken : { sourceSpan: rawToken.sourceSpan, text: correction.correctedText };
	});

	return {
		corrections: [...correctionsBySpanStart.values()],
		tokens: canonicalizeTokens(patchedRawTokens, vocabulary),
		type: 'corrected',
	};
};
